import { useEffect, useRef, type RefObject } from 'react'

interface UseInfiniteScrollOptions {
  rootRef: RefObject<HTMLElement | null>
  hasNextPage?: boolean
  onLoadMore: () => void
  /** Each new page moves the sentinel down — bumping this re-points the observer at it. */
  itemCount: number
  /** Prefetch buffer below the viewport; the 80% bottom margin loads ~one screen early. */
  rootMargin?: string
}
/**
 * Calls `onLoadMore` when the returned ref's element scrolls near the bottom of `rootRef`.
 * Could be used with <InfiniteScrollSentinel /> component.
 */
export const useInfiniteScroll = ({
  rootRef,
  hasNextPage,
  onLoadMore,
  itemCount,
  rootMargin = '0px 0px 80% 0px',
}: UseInfiniteScrollOptions): RefObject<HTMLDivElement | null> => {
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Read the latest callback without re-creating the observer every render.
  const onLoadMoreRef = useRef(onLoadMore)

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  useEffect(() => {
    const root = rootRef.current
    const target = sentinelRef.current
    if (!root || !target || !hasNextPage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) onLoadMoreRef.current()
      },
      { root, rootMargin },
    )
    observer.observe(target)

    return () => observer.disconnect()
  }, [rootRef, hasNextPage, itemCount, rootMargin])

  return sentinelRef
}
